import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, of, Subject, take, takeUntil } from 'rxjs';

// Services
import { UsersService } from '@modules/users/services/users.service';
import { NotificationService } from '@modules/elements/services/notification.service';

// Types
import { Team, UserPermissions } from '@modules/users/types/user';

interface PermissionGroup {
  title: string;
  permissions: { key: UserPermissions; label: string }[];
}

@Component({
    selector: 'app-add-team',
    templateUrl: './add-team.component.html',
    styleUrl: './add-team.component.less',
    standalone: false
})
export class AddTeamComponent implements OnInit, OnDestroy {

  // Public
  public isEdit = false;
  public name: string = '';
  public teamId?: string;
  public team?: Team;
  public selectedPermissions = new Set<UserPermissions>();
  public permissionGroups: PermissionGroup[] = [
    {
      title: 'Projects',
      permissions: [
        { key: 'project:read',   label: 'Read project' },
        { key: 'project:create', label: 'Create project' },
        { key: 'project:update', label: 'Update project' },
        { key: 'project:delete', label: 'Delete project' },
      ],
    },
    {
      title: 'Records',
      permissions: [
        { key: 'record:read',    label: 'Read record' },
        { key: 'record:create',  label: 'Create record' },
        { key: 'record:update',  label: 'Update record' },
        { key: 'record:delete',  label: 'Delete record' },
        { key: 'record:actions', label: 'Record actions' },
      ],
    },
    {
      title: 'Team',
      permissions: [
        { key: 'team:update', label: 'Update team' },
      ],
    },
    {
      title: 'Users',
      permissions: [
        { key: 'user:read',   label: 'Read users' },
        { key: 'user:invite', label: 'Invite users' },
        { key: 'user:update', label: 'Update users' },
        { key: 'user:delete', label: 'Delete users' },
      ],
    },
  ];

  // Private
  private alive = new Subject<void>();

  /**
   * Constructor
   */

  constructor(
    public dialogRef: MatDialogRef<AddTeamComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { teamId?: string },
    private usersService: UsersService,
    private notificationService: NotificationService,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    this.isEdit = !!this.data?.teamId;
    // Get Team Users
    this.usersService.getAccountTeam()
      .pipe(takeUntil(this.alive))
      .subscribe(team => this.team = team);
    // Edit mode: hydrate form from cached teams
    if (this.isEdit) {
      this.usersService.getTeams()
        .pipe(take(1), takeUntil(this.alive))
        .subscribe(teams => {
          const team = teams?.find(t => t.id === this.data.teamId);
          if (!team) {
            this.dialogRef.close();
            return;
          }
          this.name = team.name ?? '';
          this.selectedPermissions = new Set(team.permissions ?? []);
        });
    }
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  togglePermission(permission: UserPermissions): void {
    if (this.selectedPermissions.has(permission)) {
      this.selectedPermissions.delete(permission);
    } else {
      this.selectedPermissions.add(permission);
    }
  }

  isPermissionSelected(permission: UserPermissions): boolean {
    return this.selectedPermissions.has(permission);
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.name.length) {
      return;
    }
    const team = new Team();
    team.name = this.name;
    // 'admin:all' is a system permission: the server rejects it in the body and keeps it itself
    team.permissions = Array.from(this.selectedPermissions).filter(permission => permission !== 'admin:all');
    if (this.isEdit) {
      team.id = this.data.teamId;
    }
    const request = this.isEdit
      ? this.usersService.updateTeam(team)
      : this.usersService.createTeam(team);
    request
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error(this.isEdit ? 'Error update team' : 'Error create team', message);
          }
          return of();
        }),
        takeUntil(this.alive)
      )
      .subscribe(() => this.dialogRef.close());
  }

}
